import { createAsset, deleteAsset, getVariant, presignAsset } from '@cimpress-technology/sherbert-sdk'
import { getError, getFileExtension, getFileSizeBucket, logger } from '@shared/lib/logger'
import { attempt } from '@shared/utils'
import type { UploadedAsset } from '@shared/types'
import md5 from 'md5'
import React, { createContext, useContext, useMemo } from 'react'

const ASSET_STORE_TENANT = 'JqzyMexb118PYH7BJBqKY'
const ASSET_EXPIRY = 'P2Y'

export interface AssetStoreContextType {
  uploadAsset: (file: File, onProgress?: (pct: number) => void) => Promise<UploadedAsset>
  deleteAssetById: (assetId: string) => Promise<void>
}

const AssetStoreContext = createContext<AssetStoreContextType | null>(null)

interface AssetStoreProviderProps {
  children: React.ReactNode
  auth?: {
    getAuthorizationHeader?: () => string
  }
}

const generateUniqueFileName = (originalFileName: string): string => {
  const lastDotIndex = originalFileName.lastIndexOf('.')
  const name = originalFileName.substring(0, lastDotIndex).toLowerCase()
  const extension = originalFileName.substring(lastDotIndex).toLowerCase()
  return md5(name + Date.now()) + extension
}

export const AssetStoreProvider: React.FC<AssetStoreProviderProps> = ({ children, auth }) => {
  const getAuthHeader = (): string => auth?.getAuthorizationHeader?.() || ''

  const uploadAsset = async (file: File, onProgress?: (pct: number) => void): Promise<UploadedAsset> => {
    const fileName = generateUniqueFileName(file.name)
    const startedAt = Date.now()
    const extension = getFileExtension(file.name)
    const sizeBucket = getFileSizeBucket(file.size)

    logger.info('Asset upload started', {
      action: 'asset_upload_started',
      component: 'AssetStoreProvider',
      fileExtension: extension,
      fileSizeBucket: sizeBucket,
      fileSizeBytes: file.size,
      tenant: ASSET_STORE_TENANT,
    })

    if (!getAuthHeader()) {
      logger.warn('Asset upload started without authorization header', {
        action: 'asset_upload_missing_auth_header',
        component: 'AssetStoreProvider',
        tenant: ASSET_STORE_TENANT,
      })
    }

    const { data: asset, error: createError } = await attempt(
      createAsset({
        body: {
          file,
          expires: ASSET_EXPIRY,
          tenant: ASSET_STORE_TENANT,
          info: { asset: { hidden: true } },
        },
        authorizationHeader: getAuthHeader,
        onProgress: onProgress ? ({ loaded, total }) => onProgress(total > 0 ? Math.round((loaded / total) * 80) : 0) : undefined,
      }),
    )

    if (createError) {
      logger.error(
        'Asset upload failed',
        {
          action: 'asset_upload_failed',
          component: 'AssetStoreProvider',
          durationMs: Date.now() - startedAt,
          fileExtension: extension,
          fileSizeBucket: sizeBucket,
          fileSizeBytes: file.size,
          tenant: ASSET_STORE_TENANT,
        },
        getError(createError),
      )
      throw createError
    }

    logger.info('Asset created in store', {
      action: 'asset_upload_asset_created',
      component: 'AssetStoreProvider',
      assetId: asset.id,
    })

    const { data: signature, error: presignError } = await attempt(
      presignAsset({
        assetId: asset.id,
        authorizationHeader: getAuthHeader,
      }),
    )

    if (presignError) {
      logger.error(
        'Asset upload failed',
        {
          action: 'asset_upload_failed',
          component: 'AssetStoreProvider',
          durationMs: Date.now() - startedAt,
          fileExtension: extension,
          fileSizeBucket: sizeBucket,
          fileSizeBytes: file.size,
          tenant: ASSET_STORE_TENANT,
        },
        getError(presignError),
      )
      throw presignError
    }

    logger.info('Asset signature created', {
      action: 'asset_upload_presigned',
      component: 'AssetStoreProvider',
      assetId: asset.id,
    })

    const { data: variantData, error: variantError } = await attempt(
      getVariant({
        variants: [{ variant: 'original' }],
        assetId: asset.id,
        assetVersion: asset.version,
        signature: signature.signature,
      }),
    )

    if (variantError) {
      logger.error(
        'Asset upload failed',
        {
          action: 'asset_upload_failed',
          component: 'AssetStoreProvider',
          durationMs: Date.now() - startedAt,
          fileExtension: extension,
          fileSizeBucket: sizeBucket,
          fileSizeBytes: file.size,
          tenant: ASSET_STORE_TENANT,
        },
        getError(variantError),
      )
      throw variantError
    }

    onProgress?.(100)

    logger.info('Asset upload succeeded', {
      action: 'asset_upload_succeeded',
      component: 'AssetStoreProvider',
      assetId: asset.id,
      durationMs: Date.now() - startedAt,
      fileExtension: extension,
      fileSizeBucket: sizeBucket,
    })

    return {
      publicUrl: variantData.variantUrl,
      fileName,
      name: file.name,
      id: asset.id,
    }
  }

  const deleteAssetById = async (assetId: string): Promise<void> => {
    if (!assetId) {
      logger.warn('Asset deletion skipped because no asset ID was provided', {
        action: 'asset_delete_missing_id',
        component: 'AssetStoreProvider',
      })

      return
    }
    const { error: deleteError } = await attempt(deleteAsset({ assetId, authorizationHeader: getAuthHeader }))

    if (deleteError) {
      logger.error(
        'Asset deletion failed',
        {
          action: 'asset_delete_failed',
          component: 'AssetStoreProvider',
          assetId,
        },
        getError(deleteError),
      )
      return
    }

    logger.info('Asset deleted', {
      action: 'asset_delete_succeeded',
      component: 'AssetStoreProvider',
      assetId,
    })
  }

  const contextValue = useMemo<AssetStoreContextType>(() => ({ uploadAsset, deleteAssetById }), [auth])

  return <AssetStoreContext.Provider value={contextValue}>{children}</AssetStoreContext.Provider>
}

export const useAssetStore = (): AssetStoreContextType => {
  const context = useContext(AssetStoreContext)
  if (!context) {
    throw new Error('AssetStoreContext must be used within a Provider')
  }
  return context
}
